import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import io from 'socket.io-client';
import OrderService from '../order/OrderService';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

// The queue holds two legs, and an ส่งตรวจ order has BOTH: its ส่งตรวจ photo and
// its warehouse photo are two separate checks under the SAME Order.id. So:
//
//   queueKey  ('order:<id>' | 'appraisal:<id>') is the identity for EVERYTHING on
//             this screen: React keys, the current selection, history, and the
//             socket events that remove a finished item from other admins' queues.
//   verifyLeg decides which endpoint records the verdict.
//
// `id` is only ever passed to the endpoint verifyLeg picked. Using it as identity
// anywhere else would make an order's two legs one row — finishing the ส่งตรวจ
// check would remove the warehouse check from someone else's screen.
const recordVerdict = (item, status) =>
  item.verifyLeg === 'appraisal'
    ? OrderService.updateAppraisalVerifyStatus(item.id, status)
    : OrderService.updateMercariVerifyStatus(item.id, status);

const isAppraisal = (item) => item?.verifyLeg === 'appraisal';

const MercariVerifyQueue = () => {
  const shopName = useSelector((state) => state.shop.name);
  const { authState } = useAuth();
  const adminName = authState?.user?.nickname || authState?.user?.name || authState?.user?.username || 'Admin';

  const [orders, setOrders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [verifiedHistory, setVerifiedHistory] = useState([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  // socketId → { queueKey, adminName } — who else is viewing what
  const [viewingMap, setViewingMap] = useState({});

  const ordersRef = useRef(orders);
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => { ordersRef.current = orders; }, [orders]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

  const socketRef = useRef(null);

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await OrderService.getMercariPendingVerify({ shop: shopName });
      setOrders(res.data.orders);
      setCurrentIndex(0);
    } catch {
      toast.error('โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  }, [shopName]);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // Socket: multi-admin awareness for verify queue
  useEffect(() => {
    // withCredentials: the namespace admits admins only, proven by the
    // admin_token cookie on the handshake (B5).
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/mercari-verify`, { withCredentials: true });
    socketRef.current = socket;

    socket.on('verify_viewing', ({ queueKey, adminName: name, socketId }) => {
      setViewingMap((prev) => ({ ...prev, [socketId]: { queueKey, adminName: name } }));
    });

    socket.on('verify_item_done', ({ queueKey, adminName: name }) => {
      const currentOrders = ordersRef.current;
      const removedIdx = currentOrders.findIndex((o) => o.queueKey === queueKey);
      if (removedIdx === -1) return;
      setOrders((prev) => prev.filter((o) => o.queueKey !== queueKey));
      setCurrentIndex((prev) => {
        if (removedIdx < prev) return prev - 1; // item before me removed → shift left to stay on same item
        if (removedIdx === prev) {
          toast.info(`${name} ตรวจรายการนี้แล้ว`);
          return Math.min(prev, Math.max(0, currentOrders.length - 2));
        }
        return prev; // item after me removed → no change
      });
    });

    socket.on('verify_admin_left', ({ socketId }) => {
      setViewingMap((prev) => {
        const next = { ...prev };
        delete next[socketId];
        return next;
      });
    });

    return () => socket.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = orders[currentIndex] ?? null;
  const selectedHistoryOrder = verifiedHistory.find((o) => o.queueKey === selectedHistoryId) ?? null;
  const displayOrder = selectedHistoryOrder ?? current;

  // Broadcast which item we're currently viewing
  useEffect(() => {
    const currentOrder = orders[currentIndex];
    if (currentOrder && socketRef.current) {
      socketRef.current.emit('verify_viewing', { queueKey: currentOrder.queueKey, adminName });
    }
  }, [currentIndex, orders, adminName]);

  // Preload next item's images
  useEffect(() => {
    const next = orders[currentIndex + 1];
    if (!next) return;
    [next.mercariVerifyImageUrl, next.mercariVerifyImageUrl2].forEach((url) => {
      if (url && url !== 'pending_upload') {
        const img = new Image();
        img.src = url;
      }
    });
  }, [currentIndex, orders]);

  const handleVerify = async (status) => {
    if (!current) return;
    setSaving(true);
    try {
      await recordVerdict(current, status);
      toast.success(status === 'approved' ? '✓ อนุมัติแล้ว' : '✕ บันทึกไม่อนุมัติ');
      if (socketRef.current) {
        socketRef.current.emit('verify_item_done', { queueKey: current.queueKey, adminName });
      }
      setVerifiedHistory((prev) => [{ ...current, verifiedStatus: status }, ...prev]);
      setOrders((prev) => prev.filter((_, i) => i !== currentIndex));
      setCurrentIndex((prev) => Math.min(prev, Math.max(0, orders.length - 2)));
    } catch {
      toast.error('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const handleHistoryVerify = async (order, newStatus) => {
    if (newStatus === order.verifiedStatus) {
      setSelectedHistoryId(null);
      return;
    }
    setSaving(true);
    try {
      await recordVerdict(order, newStatus);
      toast.success(newStatus === 'approved' ? '✓ อนุมัติแล้ว' : '✕ บันทึกไม่อนุมัติ');
      setVerifiedHistory((prev) =>
        prev.map((o) => o.queueKey === order.queueKey ? { ...o, verifiedStatus: newStatus } : o)
      );
      setSelectedHistoryId(null);
    } catch {
      toast.error('บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const remaining = orders.length;

  const getViewers = (queueKey) =>
    Object.values(viewingMap)
      .filter((v) => v.queueKey === queueKey)
      .map((v) => v.adminName);

  return (
    <>
    <div className="h-screen grid grid-cols-3 gap-x-6 p-4 overflow-hidden">

      {/* ── LEFT: focused review panel ── */}
      <div className="col-span-2 flex flex-col gap-3 overflow-hidden">

        {/* Header row */}
        <div className="flex items-center justify-between shrink-0">
          <h1 className="text-2xl font-bold text-gray-800">Mercari Verify</h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">
              รอตรวจ{' '}
              <span className="text-xl font-bold text-orange-600">{remaining}</span>{' '}
              รายการ
            </span>
            <button
              onClick={fetchPending}
              disabled={loading}
              className="flex items-center gap-1 text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              รีโหลด
            </button>
          </div>
        </div>

        {/* Images */}
        <div className="flex-1 min-h-0 flex gap-2 overflow-hidden">
          {!displayOrder ? (
            <div className="flex-1 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-200">
              <div className="text-center text-gray-400">
                <CheckCircleIcon className="w-20 h-20 mx-auto mb-3 text-green-400" />
                <p className="text-xl font-medium text-gray-500">ตรวจครบทุกรายการแล้ว</p>
              </div>
            </div>
          ) : (() => {
            const imageSlots = [displayOrder.mercariVerifyImageUrl, displayOrder.mercariVerifyImageUrl2].filter(url => url != null);
            if (imageSlots.length === 0) imageSlots.push(undefined);
            return imageSlots.map((url, idx) => (
              <div
                key={idx}
                className={`flex-1 flex items-center justify-center bg-gray-50 rounded-xl border overflow-hidden ${selectedHistoryOrder ? 'border-yellow-300' : 'border-gray-200'}`}
              >
                {url && url !== 'pending_upload' ? (
                  <img
                    key={`${displayOrder.queueKey}-${idx}`}
                    src={url}
                    alt={`verify ${idx + 1}`}
                    className="w-full h-full object-contain cursor-zoom-in"
                    onClick={() => setLightboxUrl(url)}
                  />
                ) : url === 'pending_upload' ? (
                  <p className="text-xl text-gray-400">⏳ กำลังอัปโหลด...</p>
                ) : (
                  <p className="text-xl text-gray-400">— ไม่มีรูปภาพ —</p>
                )}
              </div>
            ));
          })()
          }
        </div>

        {/* Tracking number — big & prominent */}
        {displayOrder && (
          <div className={`shrink-0 bg-white rounded-xl border-2 px-6 py-3 flex items-baseline gap-4 ${
            selectedHistoryOrder ? 'border-yellow-300' : isAppraisal(displayOrder) ? 'border-amber-300' : 'border-blue-200'
          }`}>
            {/* Say which number this is. On an ส่งตรวจ row the photo must match the
                seller → inspection-centre number, not the warehouse one. */}
            <span className="text-base text-gray-400 font-medium shrink-0">
              {isAppraisal(displayOrder) ? (
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                  🔍 Tracking ส่งตรวจ
                </span>
              ) : 'Tracking JP'}
            </span>
            <span className={`text-5xl font-bold tracking-widest break-all ${isAppraisal(displayOrder) ? 'text-amber-700' : 'text-blue-700'}`}>
              {displayOrder.trackingJp || '—'}
            </span>
            {selectedHistoryOrder && (
              <span className={`ml-auto shrink-0 text-sm font-bold px-3 py-1 rounded-full ${
                selectedHistoryOrder.verifiedStatus === 'approved'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {selectedHistoryOrder.verifiedStatus === 'approved' ? 'อนุมัติแล้ว' : 'ไม่อนุมัติแล้ว'}
              </span>
            )}
          </div>
        )}

        {/* Action buttons — hidden when reviewing history item */}
        {current && !selectedHistoryOrder && (
          <div className="shrink-0 flex gap-4">
            <button
              onClick={() => handleVerify('approved')}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-green-500 text-white text-2xl font-bold hover:bg-green-600 disabled:opacity-40 active:scale-95 transition-transform"
            >
              <CheckCircleIcon className="w-8 h-8" />
              อนุมัติ
            </button>
            <button
              onClick={() => handleVerify('not_approved')}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-red-500 text-white text-2xl font-bold hover:bg-red-600 disabled:opacity-40 active:scale-95 transition-transform"
            >
              <XCircleIcon className="w-8 h-8" />
              ไม่อนุมัติ
            </button>
          </div>
        )}
      </div>

      {/* ── RIGHT: queue list + verified history ── */}
      <div className="border-l border-gray-300 pl-4 flex flex-col overflow-hidden gap-3">

        {/* Queue — 70% */}
        <div className="flex flex-col overflow-hidden" style={{ flex: '7' }}>
          <span className="text-xl font-bold text-gray-600 pb-3 shrink-0">รายการที่รอตรวจ</span>
          <div className="flex-1 overflow-y-auto space-y-2">
            {orders.length === 0 && !loading && (
              <p className="text-sm text-gray-400 mt-4 text-center">ไม่มีรายการ</p>
            )}
            {orders.map((order, idx) => (
              <button
                key={order.queueKey}
                onClick={() => setCurrentIndex(idx)}
                disabled={!!selectedHistoryId}
                className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                  selectedHistoryId
                    ? 'opacity-40 cursor-not-allowed border-gray-200'
                    : idx === currentIndex
                    ? 'border-blue-400 bg-blue-50'
                    : getViewers(order.queueKey).length > 0
                    ? 'border-orange-400 bg-orange-50 hover:bg-orange-100'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex gap-1 shrink-0">
                    {[order.mercariVerifyImageUrl, order.mercariVerifyImageUrl2].map((url, idx) =>
                      url && url !== 'pending_upload' ? (
                        <img key={idx} src={url} alt="" className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div key={idx} className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-300 text-xs">
                          {url === 'pending_upload' ? '⏳' : idx === 0 ? '—' : ''}
                        </div>
                      )
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{order.smileNameInput || '—'}</p>
                    {isAppraisal(order) && (
                      <span className="inline-block text-[10px] font-semibold px-1.5 py-px rounded bg-amber-100 text-amber-800 border border-amber-300">
                        🔍 ส่งตรวจ
                      </span>
                    )}
                    <p className={`text-xs font-mono truncate ${isAppraisal(order) ? 'text-amber-700' : 'text-blue-600'}`}>{order.trackingJp || '—'}</p>
                    <p className="text-xs text-gray-400">{formatDate(order.orderDate)}</p>
                    {getViewers(order.queueKey).length > 0 && (
                      <p className="text-xs text-orange-500 font-medium truncate">
                        {getViewers(order.queueKey).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Verified history — 30% */}
        <div className="flex flex-col overflow-hidden border-t border-gray-200 pt-3" style={{ flex: '3' }}>
          <div className="shrink-0 pb-2">
            <span className="text-sm font-semibold text-gray-500">ตรวจแล้ว (session)</span>
            <p className="text-xs text-gray-400 mt-0.5">คลิกรายการเพื่อแก้ไขผล</p>
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {verifiedHistory.length === 0 && (
              <p className="text-xs text-gray-300 mt-2 text-center">ยังไม่มีรายการ</p>
            )}
            {verifiedHistory.map((order) => (
              <div
                key={order.queueKey}
                className={`rounded-lg border px-3 py-1.5 transition-colors cursor-pointer ${
                  selectedHistoryId === order.queueKey
                    ? 'border-yellow-400 bg-yellow-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
                onClick={() => setSelectedHistoryId(selectedHistoryId === order.queueKey ? null : order.queueKey)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700 truncate">
                      {isAppraisal(order) && <span className="text-amber-700">🔍 </span>}
                      {order.smileNameInput || '—'}
                    </p>
                    <p className="text-xs text-blue-600 font-mono truncate">{order.trackingJp || '—'}</p>
                  </div>
                  <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${
                    order.verifiedStatus === 'approved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {order.verifiedStatus === 'approved' ? 'อนุมัติ' : 'ไม่อนุมัติ'}
                  </span>
                </div>
                {selectedHistoryId === order.queueKey && (
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleHistoryVerify(order, 'approved'); }}
                      disabled={saving}
                      className={`flex-1 text-xs font-bold py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${
                        order.verifiedStatus === 'approved'
                          ? 'bg-green-500 text-white border-green-500'
                          : 'bg-white text-green-600 border-green-400 hover:bg-green-50'
                      }`}
                    >
                      ✓ อนุมัติ
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleHistoryVerify(order, 'not_approved'); }}
                      disabled={saving}
                      className={`flex-1 text-xs font-bold py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${
                        order.verifiedStatus === 'not_approved'
                          ? 'bg-red-500 text-white border-red-500'
                          : 'bg-white text-red-600 border-red-400 hover:bg-red-50'
                      }`}
                    >
                      ✕ ไม่อนุมัติ
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
        >
          <img
            src={lightboxUrl}
            alt="enlarged"
            className="max-h-screen max-w-screen object-contain"
          />
        </div>
      )}
    </>
  );
};

export default MercariVerifyQueue;
